import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import { downloadFromUrl, getPublicUrl } from "@/shared/lib/downloadFile";
import type { AssetDocument } from "./types";

/**
 * Loads asset_documents for an asset and splits them into photos / docs.
 * Returns the lists plus a refetch and a download handler.
 */
export function useAssetDocuments(assetId: string | null | undefined, isOpen: boolean) {
  const [documents, setDocuments] = useState<AssetDocument[]>([]);
  const [photos, setPhotos] = useState<AssetDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const fetchDocumentsAndPhotos = useCallback(async () => {
    if (!assetId) return;

    setLoadingDocuments(true);
    try {
      const { data, error } = await supabase
        .from('asset_documents')
        .select('id, document_name, document_type, file_path, mime_type, created_at, category, uploaded_by, file_size, tags')
        .is('deleted_at', null)
        .eq('asset_id', assetId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const allDocs = (data || []) as AssetDocument[];
      setPhotos(allDocs.filter(doc => doc.document_type === 'photo'));
      setDocuments(allDocs.filter(doc => doc.document_type !== 'photo'));
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  }, [assetId]);

  // Clear state when switching assets
  useEffect(() => {
    if (!assetId || !isOpen) return;
    setDocuments([]);
    setPhotos([]);
  }, [assetId, isOpen]);

  useEffect(() => {
    if (isOpen && assetId) {
      fetchDocumentsAndPhotos();
    }
  }, [isOpen, assetId, fetchDocumentsAndPhotos]);

  return { documents, photos, loadingDocuments, fetchDocumentsAndPhotos };
}

export async function downloadAssetDocument(doc: AssetDocument) {
  const publicUrl = getPublicUrl(doc);
  if (publicUrl) {
    const filename = (doc.document_name || "document").replace(/^.*[\\/]/, "");
    try {
      await downloadFromUrl(publicUrl, filename);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
    return;
  }
  try {
    const { data, error } = await supabase.storage
      .from('asset-documents')
      .download(doc.file_path);

    if (error) throw error;

    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.document_name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading document:', error);
  }
}
